/**
 * Cart Coupons Drawer Handler
 * Opens/closes coupons drawer overlay above cart drawer
 * Handles copy to clipboard functionality with toast notifications
 */

(function () {
  "use strict";

  const selectors = {
    couponsDrawer: "#CouponsDrawer",
    openButton: "[data-coupons-drawer-open]",
    closeButton: ".js-coupons-drawer-close",
    backButton: ".js-coupons-drawer-back",
    applyButtons: "[data-coupon-apply]",
    toastContainer: "[data-coupons-toast]",
    couponCards: "[data-coupon-card]",
    discountInput: "#CartDrawerDiscountCode",
    discountApply: "[data-discount-apply]",
  };

  const classes = {
    open: "drawer--is-open",
    toast: "coupons-toast",
    toastEntering: "coupons-toast--entering",
    toastExiting: "coupons-toast--exiting",
  };

  class CouponsDrawer {
    constructor() {
      this.drawer = document.querySelector(selectors.couponsDrawer);
      if (!this.drawer) {
        return;
      }

      this.openBtn = document.querySelector(selectors.openButton);
      this.closeBtn = this.drawer.querySelector(selectors.closeButton);
      this.backBtn = this.drawer.querySelector(selectors.backButton);
      this.toastContainer = null;
      this.isOpen = false;
      this.currentToastTimeout = null;
      this.abortController = new AbortController();
      this.appliedCouponCode = null;
      this.pendingCouponCode = null;
      this.init();
    }

    init() {
      if (!this.drawer || !this.openBtn) {
        return;
      }

      this.createToastContainer();

      const signal = this.abortController.signal;

      this.openBtn.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.open();
        },
        { signal }
      );

      if (this.closeBtn) {
        this.closeBtn.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            this.close();
          },
          { signal }
        );
      }

      if (this.backBtn) {
        this.backBtn.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            this.goBack();
          },
          { signal }
        );
      }

      // Apply buttons (will be re-initialized on open)
      this.initApplyButtons();

      // Bind discount form inside coupons popup to reuse main cart discount logic
      this.bindPopupDiscountForm();

      document.addEventListener("cart:updated", () => {
        this.syncAppliedState();
      }, { signal });

      document.addEventListener("cart:build", () => {
        this.syncAppliedState();
      }, { signal });

      this.drawer.addEventListener(
        "click",
        (e) => {
          if (e.target === this.drawer && !e.target.closest('.drawer__contents')) {
            this.close();
          }
        },
        { signal }
      );

      document.addEventListener(
        "keydown",
        (e) => {
          if (e.key === "Escape" && this.isOpen) {
            this.close();
          }
        },
        { signal }
      );

      document.addEventListener(
        "cart:close",
        () => {
          if (this.isOpen) {
            this.close();
          }
        },
        { signal }
      );

      document.addEventListener(
        "drawerOpen.CartDrawer",
        () => {
          this.syncAppliedState();
        },
        { signal }
      );

      document.addEventListener(
        "drawerClose.CartDrawer",
        () => {
          if (this.isOpen) {
            this.close();
          }
        },
        { signal }
      );

      document.addEventListener(
        "click",
        (e) => {
          if (!this.isOpen) return;

          const cartDrawer = document.querySelector("#CartDrawer");
          const isCartOpen = cartDrawer && cartDrawer.classList.contains("drawer--is-open");

          const isClickOnCouponsDrawer = this.drawer && (
            this.drawer.contains(e.target) ||
            e.target === this.drawer ||
            (this.drawer.querySelector('.drawer__contents') &&
              this.drawer.querySelector('.drawer__contents').contains(e.target))
          );

          // Check if click is on cart drawer or its content
          const isClickOnCartDrawer = cartDrawer && (
            cartDrawer.contains(e.target) ||
            e.target === cartDrawer ||
            (cartDrawer.querySelector('.drawer__contents') &&
              cartDrawer.querySelector('.drawer__contents').contains(e.target))
          );

          if (!isCartOpen && !isClickOnCouponsDrawer) {
            this.close();
          }

          if (isCartOpen && !isClickOnCartDrawer && !isClickOnCouponsDrawer) {
            this.close();
          }
        },
        { signal }
      );
    }

    createToastContainer() {
      let container = document.querySelector('[data-coupons-toast]');
      if (!container) {
        container = document.createElement("div");
        container.setAttribute("data-coupons-toast", "");
        container.className = "coupons-toast-container";
        document.body.appendChild(container);
      }
      this.toastContainer = container;
    }

    initApplyButtons() {
      if (!this.drawer) return;

      const applyButtons = this.drawer.querySelectorAll(selectors.applyButtons);
      applyButtons.forEach((btn) => {
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);

        const code = newBtn.getAttribute("data-coupon-apply");
        if (!code) return;

        newBtn.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleApply(code, newBtn);
          },
          { signal: this.abortController.signal }
        );
      });

      this.syncAppliedState();
    }

    bindPopupDiscountForm() {
      if (!this.drawer) return;

      const popupContainer = this.drawer.querySelector(".cart-discount-code--popup");
      if (!popupContainer) return;

      const popupInput = popupContainer.querySelector("[data-popup-discount-input]");
      const popupApplyOriginal = popupContainer.querySelector("[data-popup-discount-apply]");

      if (!popupInput || !popupApplyOriginal) return;

      const self = this;

      const getCouponMinOrderAmount = (code) => {
        const couponCard = this.drawer.querySelector(
          `[data-coupon-card] [data-coupon-code="${code.toUpperCase()}"]`
        )?.closest("[data-coupon-card]");

        if (!couponCard) return null;

        const minOrderAmountRaw = couponCard.getAttribute("data-min-order-amount") || "0";
        return parseFloat(minOrderAmountRaw.replace(/,/g, "")) || 0;
      };

      const newApplyBtn = popupApplyOriginal.cloneNode(true);
      popupApplyOriginal.parentNode.replaceChild(newApplyBtn, popupApplyOriginal);

      const popupApply = newApplyBtn;

      const showPopupLoader = () => {
        popupApply.disabled = true;
        popupApply.classList.add("is-loading");
        popupInput.disabled = true;

        const loader = popupApply.querySelector(".cart-discount-code-apply-loader");
        const text = popupApply.querySelector(".cart-discount-code-apply-text");
        if (loader) loader.style.display = "inline-block";
        if (text) text.style.display = "none";
      };

      const hidePopupLoader = () => {
        popupApply.disabled = false;
        popupApply.classList.remove("is-loading");
        popupInput.disabled = false;

        const loader = popupApply.querySelector(".cart-discount-code-apply-loader");
        const text = popupApply.querySelector(".cart-discount-code-apply-text");
        if (loader) loader.style.display = "none";
        if (text) text.style.display = "inline";
      };

      let isApplying = false;
      let appliedCode = null;
      let fallbackTimeoutId = null;

      const isDiscountCodeApplied = (cart, code) => {
        const upper = (code || "").toUpperCase();
        const cartLevel = cart.cart_level_discount_applications || [];
        if (cartLevel.some((d) => (d.title || "").toUpperCase() === upper)) return true;
        const items = cart.items || [];
        for (let i = 0; i < items.length; i++) {
          const allocs = items[i].line_level_discount_allocations || items[i].discount_allocations || [];
          if (allocs.some((a) => a.discount_application && (a.discount_application.title || "").toUpperCase() === upper)) return true;
        }
        return false;
      };

      const handleCartUpdate = async () => {
        if (!isApplying) return;

        try {
          const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
            credentials: "same-origin",
            method: "GET",
          });

          if (response.ok) {
            const cart = await response.json();
            const wasApplied = isDiscountCodeApplied(cart, appliedCode);

            if (wasApplied) {
              if (fallbackTimeoutId) {
                clearTimeout(fallbackTimeoutId);
                fallbackTimeoutId = null;
              }

              popupInput.value = "";
              hidePopupLoader();
              isApplying = false;
              appliedCode = null;

              document.removeEventListener("cart:updated", handleCartUpdate);
              document.removeEventListener("cart:build", handleCartUpdate);

              requestAnimationFrame(() => {
                self.close();
              });
            } else if (appliedCode) {
              setTimeout(() => {
                if (!isApplying) return;

                fetch(`${window.Shopify.routes.root}cart.js`, {
                  credentials: "same-origin",
                  method: "GET",
                })
                  .then(res => res.json())
                  .then(cart => {
                    const stillNotApplied = !isDiscountCodeApplied(cart, appliedCode);
                    const discounts = cart.cart_level_discount_applications || [];

                    if (stillNotApplied && isApplying) {
                      if (fallbackTimeoutId) {
                        clearTimeout(fallbackTimeoutId);
                        fallbackTimeoutId = null;
                      }

                      const hasExistingDiscount = discounts.length > 0;
                      let shouldShowError = false;
                      let errorMessage = "";

                      if (hasExistingDiscount) {
                        const existingDiscountCode = discounts[0].title.toUpperCase();
                        const newCouponMinOrder = getCouponMinOrderAmount(appliedCode);
                        const existingCouponMinOrder = getCouponMinOrderAmount(existingDiscountCode);

                        if (newCouponMinOrder !== null && existingCouponMinOrder !== null) {
                          shouldShowError = true;
                          errorMessage = "This code cannot be applied with existing offer";
                        }
                      }

                      popupInput.value = "";

                      if (shouldShowError) {
                        hidePopupLoader();
                        self.close();
                        self.showCartToast(errorMessage, "error");
                      } else {
                        hidePopupLoader();
                        self.close();
                        self.showCartToast("Invalid or inapplicable discount code", "error");
                      }

                      isApplying = false;
                      appliedCode = null;
                      document.removeEventListener("cart:updated", handleCartUpdate);
                      document.removeEventListener("cart:build", handleCartUpdate);
                    }
                  })
                  .catch(err => {
                    console.error("Error verifying discount:", err);
                    if (isApplying) {
                      hidePopupLoader();
                      isApplying = false;
                      appliedCode = null;
                      document.removeEventListener("cart:updated", handleCartUpdate);
                      document.removeEventListener("cart:build", handleCartUpdate);
                    }
                  });
              }, 300); // Reduced delay for faster feedback
            }
          }
        } catch (error) {
          console.error("Error checking cart update:", error);

          if (fallbackTimeoutId) {
            clearTimeout(fallbackTimeoutId);
            fallbackTimeoutId = null;
          }

          hidePopupLoader();
          isApplying = false;
          appliedCode = null;
          document.removeEventListener("cart:updated", handleCartUpdate);
          document.removeEventListener("cart:build", handleCartUpdate);
        }
      };

      let popupToastTimeout = null;
      const showPopupToast = (message, type = "info") => {
        const popupToastContainer = this.drawer.querySelector("[data-coupons-drawer-toast]");
        if (!popupToastContainer) return;

        // Remove all existing toasts immediately so repeated clicks never stack
        popupToastContainer.querySelectorAll(".cart-drawer-toast").forEach((t) => t.remove());

        if (popupToastTimeout) {
          clearTimeout(popupToastTimeout);
          popupToastTimeout = null;
        }

        requestAnimationFrame(() => {
          const toast = document.createElement("div");
          toast.className = `cart-drawer-toast cart-drawer-toast--${type}`;
          toast.textContent = message;
          toast.setAttribute("role", "alert");
          toast.setAttribute("aria-live", "polite");
          popupToastContainer.appendChild(toast);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              toast.classList.add("cart-drawer-toast--entering");
            });
          });

          popupToastTimeout = setTimeout(() => {
            toast.classList.remove("cart-drawer-toast--entering");
            toast.classList.add("cart-drawer-toast--exiting");
            setTimeout(() => {
              if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
              }
            }, 250);
          }, 5000);
        });
      };

      const handlePopupApply = async () => {
        if (fallbackTimeoutId) {
          clearTimeout(fallbackTimeoutId);
          fallbackTimeoutId = null;
        }

        if (isApplying) {
          return;
        }

        const code = popupInput.value.trim().toUpperCase();
        if (!code) {
          showPopupToast("Please enter a discount code", "error");
          return;
        }

        try {
          const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
            credentials: "same-origin",
            method: "GET",
          });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            const discounts = cart.cart_level_discount_applications || [];
            const isDuplicate = discounts.some(
              (d) => d.title.toUpperCase() === code.toUpperCase()
            );
            if (isDuplicate) {
              showPopupToast("This discount code is already applied", "error");
              return;
            }
          }
        } catch (error) {
          console.error("Error checking for duplicate:", error);
        }

        showPopupLoader();
        isApplying = true;
        appliedCode = code;

        document.addEventListener("cart:updated", handleCartUpdate);
        document.addEventListener("cart:build", handleCartUpdate);

        const mainContainer = document.querySelector("[data-drawer-discount]");
        if (!mainContainer) {
          hidePopupLoader();
          showPopupToast("Something went wrong. Please try again.", "error");
          isApplying = false;
          appliedCode = null;
          return;
        }

        const cartInput = mainContainer.querySelector("[data-discount-input]");
        const cartApply = mainContainer.querySelector("[data-discount-apply]");

        if (!cartInput || !cartApply) {
          hidePopupLoader();
          showPopupToast("Something went wrong. Please try again.", "error");
          isApplying = false;
          appliedCode = null;
          return;
        }

        cartInput.value = code;

        const inputEvent = new Event("input", {
          bubbles: true,
          cancelable: true,
        });
        cartInput.dispatchEvent(inputEvent);

        cartApply.dataset.popupTriggered = "true";
        cartApply.click();

        fallbackTimeoutId = setTimeout(async () => {
          if (!isApplying) return;

          try {
            const verifyResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
              credentials: "same-origin",
              method: "GET",
            });

            if (verifyResponse.ok) {
              const cart = await verifyResponse.json();
              const wasApplied = isDiscountCodeApplied(cart, appliedCode);

              if (wasApplied) {
                popupInput.value = "";
                hidePopupLoader();
                isApplying = false;
                appliedCode = null;
                document.removeEventListener("cart:updated", handleCartUpdate);
                document.removeEventListener("cart:build", handleCartUpdate);

                requestAnimationFrame(() => {
                  self.close();
                });
                return;
              }
            }
          } catch (error) {
            console.error("Error verifying coupon in fallback:", error);
          }

          if (isApplying) {
            try {
              const verifyResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
                credentials: "same-origin",
                method: "GET",
              });

              if (verifyResponse.ok) {
                const cart = await verifyResponse.json();
                const discounts = cart.cart_level_discount_applications || [];
                const hasExistingDiscount = discounts.length > 0;

                let shouldShowError = false;
                let errorMessage = "";

                if (hasExistingDiscount && appliedCode) {
                  shouldShowError = true;
                  errorMessage = "This code cannot be applied with existing offer";
                }

                popupInput.value = "";

                if (shouldShowError) {
                  hidePopupLoader();
                  self.close();
                  self.showCartToast(errorMessage, "error");
                } else {
                  hidePopupLoader();
                  self.close();
                  self.showCartToast("Invalid or inapplicable discount code", "error");
                }
              } else {
                hidePopupLoader();
              }
            } catch (error) {
              hidePopupLoader();
            }

            isApplying = false;
            appliedCode = null;
            document.removeEventListener("cart:updated", handleCartUpdate);
            document.removeEventListener("cart:build", handleCartUpdate);
          }
        }, 5000);
      };

      popupApply.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        handlePopupApply();
      });

      popupInput.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handlePopupApply();
        }
      });
    }

    open() {
      if (this.isOpen) return;

      this.drawer.style.display = 'block';

      if (typeof theme !== "undefined" && theme.utils && theme.utils.prepareTransition) {
        theme.utils.prepareTransition(this.drawer, () => {
          this.drawer.classList.add(classes.open);
        });
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.drawer.classList.add(classes.open);
          });
        });
      }

      document.documentElement.classList.add("js-drawer-open");

      this.isOpen = true;

      if (typeof theme !== "undefined" && theme.a11y && theme.a11y.trapFocus) {
        setTimeout(() => {
          theme.a11y.trapFocus({
            container: this.drawer,
            elementToFocus: this.drawer,
            namespace: "couponsdrawer_focus",
          });
        }, 100);
      }

      this.initApplyButtons();

      document.dispatchEvent(new CustomEvent("coupons:drawer:open"));
    }

    close() {
      if (!this.isOpen) return;

      if (typeof theme !== "undefined" && theme.utils && theme.utils.prepareTransition) {
        theme.utils.prepareTransition(this.drawer, () => {
          this.drawer.classList.remove(classes.open);
        });
      } else {
        requestAnimationFrame(() => {
          this.drawer.classList.remove(classes.open);
        });
      }

      const cartDrawer = document.querySelector("#CartDrawer");
      if (!cartDrawer || !cartDrawer.classList.contains("drawer--is-open")) {
        document.documentElement.classList.remove("js-drawer-open");
      }

      this.isOpen = false;

      if (typeof theme !== "undefined" && theme.a11y && theme.a11y.removeTrapFocus) {
        theme.a11y.removeTrapFocus({
          container: this.drawer,
          namespace: "couponsdrawer_focus",
        });
      }

      document.dispatchEvent(new CustomEvent("coupons:drawer:close"));
    }

    goBack() {
      this.close();
    }

    async handleApply(code, button) {
      if (!code) return;

      const discountInput = document.querySelector(selectors.discountInput);
      const applyButton = document.querySelector(selectors.discountApply);

      if (!discountInput || !applyButton) {
        this.showToast("Unable to apply coupon. Please try again.", "error");
        return;
      }

      this.pendingCouponCode = code.toUpperCase();

      if (button) {
        const appliedText = button.querySelector(".coupon-card__applied-text");
        if (appliedText) {
          appliedText.style.display = "none";
        }

        const applyText = button.querySelector(".coupon-card__apply-text");
        if (applyText) {
          applyText.style.display = "inline";
        }

        let loader = button.querySelector(".coupon-card__apply-loader");
        if (!loader) {
          loader = document.createElement("span");
          loader.className = "coupon-card__apply-loader";
          const spinner = document.createElement("span");
          spinner.className = "cart-discount-code-spinner";
          loader.appendChild(spinner);
          button.appendChild(loader);
        }

        button.classList.add("is-loading");
      }

      discountInput.value = code.toUpperCase();

      const inputEvent = new Event("input", {
        bubbles: true,
        cancelable: true,
      });
      discountInput.dispatchEvent(inputEvent);

      // Signal replace-mode so applyDiscount sends only this code (switch, not stack)
      applyButton.dataset.replaceDiscount = "true";

      requestAnimationFrame(() => {
        applyButton.click();
      });
    }

    async syncAppliedState() {
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
          credentials: "same-origin",
          method: "GET",
        });

        if (!response.ok) {
          console.error("Failed to fetch cart data");
          return;
        }

        const cart = await response.json();
        const discountApplications = cart.cart_level_discount_applications || [];

        const appliedCodesSet = new Set();
        discountApplications.forEach((d) => {
          if (d.title) appliedCodesSet.add(d.title.toUpperCase());
        });
        (cart.items || []).forEach((item) => {
          const allocs = item.line_level_discount_allocations || item.discount_allocations || [];
          allocs.forEach((a) => {
            const title = a.discount_application && a.discount_application.title;
            if (title) appliedCodesSet.add(title.toUpperCase());
          });
        });

        const allApplyButtons = this.drawer?.querySelectorAll(selectors.applyButtons);
        if (allApplyButtons) {
          allApplyButtons.forEach((btn) => {
            const code = btn.getAttribute("data-coupon-apply");
            if (!code) return;

            const isApplied = appliedCodesSet.has(code.toUpperCase());
            const applyText = btn.querySelector(".coupon-card__apply-text");
            const appliedText = btn.querySelector(".coupon-card__applied-text");

            if (isApplied) {
              btn.classList.remove("is-loading");
              const loader = btn.querySelector(".coupon-card__apply-loader");
              if (loader) loader.remove();

              requestAnimationFrame(() => {
                btn.disabled = true;
                if (applyText) applyText.style.display = "none";
                if (appliedText) appliedText.style.display = "inline";
              });

              if (this.pendingCouponCode && this.pendingCouponCode.toUpperCase() === code.toUpperCase()) {
                this.pendingCouponCode = null;
                requestAnimationFrame(() => { this.close(); });
              }

              this.appliedCouponCode = code.toUpperCase();
            } else {
              const hasPendingCoupon = this.pendingCouponCode &&
                this.pendingCouponCode.toUpperCase() === code.toUpperCase();

              if (!hasPendingCoupon) {
                btn.classList.remove("is-loading");
                const loader = btn.querySelector(".coupon-card__apply-loader");
                if (loader) loader.remove();
                btn.disabled = false;
                if (applyText) applyText.style.display = "inline";
                if (appliedText) appliedText.style.display = "none";
              }

              if (hasPendingCoupon) {
                setTimeout(() => {
                  if (this.pendingCouponCode && this.pendingCouponCode.toUpperCase() === code.toUpperCase()) {
                    fetch(`${window.Shopify.routes.root}cart.js`, { credentials: "same-origin" })
                      .then(res => res.json())
                      .then(cart => {
                        const discounts = cart.cart_level_discount_applications || [];
                        const stillNotApplied = !discounts.some(d => d.title.toUpperCase() === code.toUpperCase());

                        if (stillNotApplied) {
                          btn.classList.remove("is-loading");
                          const loader = btn.querySelector(".coupon-card__apply-loader");
                          if (loader) loader.remove();
                          btn.disabled = false;
                          if (applyText) applyText.style.display = "inline";
                          if (appliedText) appliedText.style.display = "none";
                          this.pendingCouponCode = null;

                          this.close();
                          const errorMsg = discounts.length > 0
                            ? "This code cannot be applied with existing offer"
                            : "This coupon is not applicable for items in your cart";
                          this.showCartToast(errorMsg, "error");
                        }
                      })
                      .catch(() => {
                        btn.classList.remove("is-loading");
                        const loader = btn.querySelector(".coupon-card__apply-loader");
                        if (loader) loader.remove();
                        btn.disabled = false;
                        if (applyText) applyText.style.display = "inline";
                        if (appliedText) appliedText.style.display = "none";
                        this.pendingCouponCode = null;
                      });
                  }
                }, 500);
              }
            }
          });
        }

        if (!appliedCodesSet.size) {
          this.appliedCouponCode = null;
        }
      } catch (error) {
        console.error("Error syncing applied state:", error);
      }
    }

    showCartToast(message, type = "success") {
      const container = document.querySelector('[data-cart-drawer-toast]');
      if (!container) return;

      const existing = container.querySelector('.cart-drawer-toast');
      if (existing) existing.remove();

      requestAnimationFrame(() => {
        const toast = document.createElement("div");
        toast.className = `cart-drawer-toast cart-drawer-toast--${type}`;
        toast.textContent = message;
        toast.setAttribute("role", "alert");
        toast.setAttribute("aria-live", "polite");
        container.appendChild(toast);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            toast.classList.add("cart-drawer-toast--entering");
          });
        });

        setTimeout(() => {
          toast.classList.remove("cart-drawer-toast--entering");
          toast.classList.add("cart-drawer-toast--exiting");
          setTimeout(() => {
            if (toast.parentNode) toast.parentNode.removeChild(toast);
          }, 250);
        }, 5000);
      });
    }

    showToast(message, type = "success") {
      if (!this.toastContainer) return;

      // Remove all existing toasts immediately so repeated clicks never stack
      this.toastContainer.querySelectorAll(`.${classes.toast}`).forEach((t) => t.remove());

      if (this.currentToastTimeout) {
        clearTimeout(this.currentToastTimeout);
        this.currentToastTimeout = null;
      }

      requestAnimationFrame(() => {
        const toast = document.createElement("div");
        toast.className = `${classes.toast} ${classes.toast}--${type}`;
        toast.textContent = message;
        toast.setAttribute("role", "alert");
        toast.setAttribute("aria-live", "polite");
        this.toastContainer.appendChild(toast);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            toast.classList.add(classes.toastEntering);
          });
        });

        this.currentToastTimeout = setTimeout(() => {
          this.hideToast();
        }, 5000);
      });
    }

    hideToast() {
      if (!this.toastContainer) return;

      const existingToast = this.toastContainer.querySelector(`.${classes.toast}`);
      if (existingToast) {
        requestAnimationFrame(() => {
          existingToast.classList.remove(classes.toastEntering);
          existingToast.classList.add(classes.toastExiting);

          setTimeout(() => {
            requestAnimationFrame(() => {
              if (existingToast.parentNode) {
                existingToast.parentNode.removeChild(existingToast);
              }
            });
          }, 250);
        });
      }

      if (this.currentToastTimeout) {
        clearTimeout(this.currentToastTimeout);
        this.currentToastTimeout = null;
      }
    }

    destroy() {
      this.abortController.abort();
      this.close();
    }
  }

  function init() {
    new CouponsDrawer();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.CouponsDrawer = CouponsDrawer;
})();
